import type { ConfigType } from "@plone/registry";
import installSettings from "./config/settings";
import heroSVG from "@plone/volto/icons/hero.svg";
import cardsSVG from "@plone/volto/icons/cards.svg";
import iconSVG from "@plone/volto/icons/select-all.svg";
import sliderSVG from "@plone/volto/icons/slider.svg";
import linkSVG from "@plone/volto/icons/link.svg";
import {
  Edit as CustomHeroEdit,
  View as CustomHeroView,
} from "./components/Blocks/CustomHero";
import {
  Edit as ButtonRowEdit,
  View as ButtonRowView,
} from "./components/Blocks/ButtonRow";
import {
  Edit as MultiCardEdit,
  View as MultiCardView,
} from "./components/Blocks/MultiCardBlock";
import {
  Edit as IconLinkRowEdit,
  View as IconLinkRowView,
} from "./components/Blocks/IconLinkRow";
import {
  Edit as EmblaCarouselEdit,
  View as EmblaCarouselView,
} from "./components/Blocks/EmblaCarousel";
import LeftColumnFacets from "./customizations/components/manage/Blocks/Search/layout/LeftColumnFacets";
import RightColumnFacets from "./customizations/components/manage/Blocks/Search/layout/RightColumnFacets";
import TopSideFacets from "./customizations/components/manage/Blocks/Search/layout/TopSideFacets";
export {
  MASTER_COLOR_LIST,
  getColorChoices,
  isColorDark,
} from "./config/colors";
import { buildRestriction } from "./config/blockRestrictions";
import blockThemes from "./config/blockThemes";
import { injectEditThemeStyles } from "./utils/injectEditThemeStyles";
import {
  Edit as RedirectEdit,
  View as RedirectView,
} from "./components/Blocks/Redirect";
import EventView from "./components/theme/View/EventView";
import NewsItemView from "./components/theme/View/NewsItemView";
import View from "./components/Blocks/Accordion/View";
import ImageComponent from '@plone/volto/components/theme/Image/Image';

// Wrapper to filter srcset and prevent small images from being selected
const MIN_WIDTH = 300;
const CustomImage = ({ item, imageField = 'image', ...rest }) => {
  if (!item?.image_scales?.[imageField]) {
    return <ImageComponent {...rest} item={item} imageField={imageField} />;
  }

  const filteredItem = {
    ...item,
    image_scales: {
      ...item.image_scales,
      [imageField]: item.image_scales[imageField].map((scale) => ({
        ...scale,
        scales: scale.scales
          ? Object.fromEntries(
              Object.entries(scale.scales).filter(
                ([, s]) => (s as { width: number }).width >= MIN_WIDTH
              )
            )
          : scale.scales,
      })),
    },
  };

  return <ImageComponent {...rest} item={filteredItem} imageField={imageField} />;
};

function applyConfig(config: ConfigType) {
  installSettings(config);

  //Custom Views
  config.views.contentTypesViews.Event = EventView;
  config.views.contentTypesViews["News Item"] = NewsItemView;

  // Customizing existing blocks
  config.blocks.blocksConfig.accordion.view = View;

  //Make lead image block available everywhere
  config.blocks.blocksConfig.leadimage.restricted = false;

  config.blocks.themes = blockThemes;

  if (typeof window !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        () => injectEditThemeStyles(blockThemes),
        { once: true },
      );
    } else {
      injectEditThemeStyles(blockThemes);
    }
  }

  //CustomHero
  config.blocks.blocksConfig.customHero = {
    id: "customHero",
    title: "Hero Block",
    icon: heroSVG,
    group: "common",
    view: CustomHeroView,
    edit: CustomHeroEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //ButtonRow
  config.blocks.blocksConfig.buttonRow = {
    id: "buttonRow",
    title: "Button Row",
    icon: cardsSVG,
    group: "common",
    view: ButtonRowView,
    edit: ButtonRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //IconLinkRow
  config.blocks.blocksConfig.iconLinkRow = {
    id: "iconLinkRow",
    title: "Icon Link Row",
    icon: iconSVG,
    group: "common",
    view: IconLinkRowView,
    edit: IconLinkRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Multicard Block
  config.blocks.blocksConfig.multiCard = {
    id: "multiCard",
    title: "Multi Card",
    icon: cardsSVG,
    group: "common",
    view: MultiCardView,
    edit: MultiCardEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Embla Carousel
  config.blocks.blocksConfig.EmblaCarousel = {
    id: "EmblaCarousel",
    title: "Embla Carousel",
    icon: sliderSVG,
    group: "common",
    view: EmblaCarouselView,
    edit: EmblaCarouselEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  // Remove the Volto Light Theme's schema enhancer
  delete config.blocks.blocksConfig.search.schemaEnhancer;

  // Restore the original search block variations
  config.blocks.blocksConfig.search.variations = [
    {
      id: "facetsTopSide",
      title: "Facets on top",
      view: TopSideFacets,
      isDefault: true,
    },
    {
      id: "facetsLeftSide",
      title: "Facets on left",
      view: LeftColumnFacets,
    },
    {
      id: "facetsRightSide",
      title: "Facets on right",
      view: RightColumnFacets,
    },
  ];

  //Redirect block
  config.blocks.blocksConfig.redirectBlock = {
    id: "redirectBlock",
    title: "Redirect",
    icon: linkSVG,
    group: "common",
    view: RedirectView,
    edit: RedirectEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 0,
    security: {
      addPermission: [],
      view: [],
    },
  };

  //Configure which blocks show and for which Editors (Keep last in line, after block registrations)
  const blocksConfig = config.blocks.blocksConfig;
  Object.keys(blocksConfig).forEach((blockId) => {
    const originalRestricted = blocksConfig[blockId].restricted;
    blocksConfig[blockId] = {
      ...blocksConfig[blockId],
      restricted: buildRestriction(blockId, originalRestricted),
    };
  });

  // Override the Image component
  config.registerComponent({
    name: 'Image',
    component: CustomImage,
  });

  return config;
}

export default applyConfig;
